/**
 * Where BroadPaper says what it did, and asks about what it could not.
 *
 * The brief is specific about this screen and the specification is worth restating,
 * because it is what the layout below is built from: *do not present the user with
 * fourteen dropdowns if twelve are already obvious.* So:
 *
 * - the first thing on screen is a sentence — "BroadPaper matched 12 of 14 fields";
 * - underneath it, **only the ones needing attention**, each as a card with the
 *   plausible columns and *the user's own values* beside them;
 * - everything settled is collapsed into a list they can open if they want to
 *   check, and which most people never will.
 *
 * And the reason the values matter more than the column names: a person checking a
 * mapping is checking the number. `£482,310` is recognised instantly;
 * `portfolio_current_value` has to be read, parsed and thought about. Every option
 * on this screen shows both, with the value first.
 *
 * **Everything here is changeable, including what BroadPaper was sure about.** The
 * folded list is not a receipt. A settled match opens into the same chooser as a
 * question, and a field the matcher left out can be given a typed value — which is
 * how somebody sets a VAT rate their spreadsheet does not contain. The first
 * version of this screen could only ask about what it was unsure of, so a
 * confident wrong answer was permanent: a VAT rate taken from a column of unit
 * prices put £917,785 on a £2,442 invoice with nothing on screen to say why and
 * nothing anywhere to change it.
 */
import { Fragment, useEffect, useState } from "react";
import type { Choice, Mapping } from "@broadpaper/mapping";
import { api, type MatchResult, type MatchRow } from "../api.js";
import { reportFailure } from "../App.js";
import { go } from "../router.js";
import { Button, Card, Chip, Empty, Icon, Note, Skeleton, count, toast } from "../ui/index.js";

export function Matching({ templateId }: { templateId: string }) {
  const [result, setResult] = useState<MatchResult | null>(null);
  const [mapping, setMapping] = useState<Mapping>({});
  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [datasetName, setDatasetName] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  /*
   * Open by default.
   *
   * It was folded on the reasoning that a dozen correct answers is noise — which
   * is true of the *first* visit and wrong about every other one. Nobody arrives
   * here to be told it worked; they arrive because a number looks wrong, and the
   * list is the answer. A fold in front of the answer is one more thing to find.
   */
  const [showSettled, setShowSettled] = useState(true);
  /** The path of the settled field being changed, if any. One at a time. */
  const [editing, setEditing] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  /** The column the file is split by. Null is "one document, every row on it". */
  const [groupBy, setGroupBy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const template = await api.template(templateId);
        if (cancelled) return;
        if (!template.dataset) {
          setFailed("This design has no data behind it yet.");
          return;
        }
        setDatasetId(template.dataset.id);
        setDatasetName(template.dataset.name);
        /*
         * The saved mapping goes back to the matcher.
         *
         * Without it every visit to this screen silently re-decided the fields,
         * so a choice somebody made last week was thrown away by looking at the
         * page. The service filters it to what this dataset can still honour, so
         * a column that has since gone is re-matched rather than kept.
         */
        const matched = await api.matchTemplate(templateId, {
          datasetId: template.dataset.id,
          ...(template.mapping ? { mapping: template.mapping } : {})
        });
        if (cancelled) return;
        setResult(matched);
        setMapping(matched.mapping);
        setGroupBy(matched.grouping.column);
      } catch (e) {
        if (!cancelled) setFailed(e instanceof Error ? e.message : "We couldn't work this out.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const choose = (path: string, choice: Choice) => {
    setMapping((current) => ({ ...current, [path]: choice }));
    setEditing(null);
  };

  /**
   * Matching again from nothing.
   *
   * The escape hatch, and the reason it exists: a kept choice is never revisited,
   * so a mapping that went wrong once stays wrong however much the matcher
   * improves. This throws the saved answers away and asks the current matcher what
   * it makes of the file.
   */
  const startAgain = async () => {
    if (!datasetId) return;
    setReloading(true);
    try {
      const matched = await api.matchTemplate(templateId, { datasetId });
      setResult(matched);
      setMapping(matched.mapping);
      setEditing(null);
      toast("Matched again from your data.", "good");
    } catch (e) {
      reportFailure(e, "We couldn't match that again.");
    } finally {
      setReloading(false);
    }
  };

  const save = async () => {
    if (!datasetId) return;
    setSaving(true);
    try {
      await api.saveTemplate(templateId, { datasetId, mapping, groupBy });
      toast("Your data is connected.", "good");
      go(`/designs/${templateId}`);
    } catch (e) {
      setSaving(false);
      reportFailure(e, "We couldn't save that.");
    }
  };

  /**
   * The outstanding questions, as the screen sees them.
   *
   * Derived from the mapping rather than from the server's original answer, so a
   * card disappears the moment it is answered — which is the whole feedback loop of
   * this screen. Re-asking the server after every click would be a round trip to be
   * told something the browser already knows.
   */
  const outstanding = (result?.questions ?? []).filter((q) => {
    const chosen = mapping[q.path];
    return !chosen || chosen.kind === "none";
  });
  const answered = (result?.questions ?? []).length - outstanding.length;

  /**
   * Every field the design has, settled ones and answered questions alike.
   *
   * A question still waiting is above, as a card, so it is left out here — the
   * same field in two places at once reads as two different fields.
   */
  const waiting = new Set(outstanding.map((q) => q.path));
  const everything = [...(result?.settled ?? []), ...(result?.questions ?? [])].filter((r) => !waiting.has(r.path));

  if (failed) {
    return (
      <Empty title="Nothing to match yet" actions={<a className="btn btn--primary" href={`/designs/${templateId}`}>Back to the design</a>}>
        {failed}
      </Empty>
    );
  }

  if (!result) {
    return (
      <div className="wrap--narrow stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 34, width: 300 }} />
        <Skeleton rows={2} height={130} />
      </div>
    );
  }

  const done = outstanding.length === 0;
  /*
   * Fields nothing is filling, counted from the live mapping.
   *
   * "Everything is matched" over a document with four blanks on it is the same
   * small untruth the summary sentence used to tell, and this screen is the last
   * place a user can catch it before they send the document.
   */
  const blanks = everything.filter((r) => !r.isList && describe(r, mapping[r.path] ?? r.choice, result.columns).blank).length;
  const heading = !done
    ? result.summary
    : blanks === 0
      ? "Everything is matched."
      : `Matched, with ${blanks === 1 ? "one field" : `${blanks} fields`} left blank.`;

  return (
    <div className="wrap--narrow stack" style={{ gap: 22 }}>
      <div className="head__title">
        <span className="eyebrow">
          <a href={`/designs/${templateId}`} style={{ textDecoration: "none" }}>
            Back to the design
          </a>
        </span>
        {/*
          The sentence, first and largest. It is the one thing on this screen
          everybody reads, and it is the product's whole claim in nine words.
        */}
        <h1>{heading}</h1>
        <p className="head__lede">
          {done
            ? `This design is filled in from “${datasetName}”. Everything below can be changed, and anything your spreadsheet does not have, you can type in.`
            : `BroadPaper filled in everything it was sure about from “${datasetName}”. These are the ones where your judgement is better than a guess.`}
        </p>
      </div>

      {answered > 0 && !done ? <Note tone="good">{answered === 1 ? "One answered" : `${answered} answered`} — {outstanding.length} to go.</Note> : null}

      {/*
        One spreadsheet, or several documents.
        
        Asked here because it is a question about the *document* — the same sales
        ledger is one monthly report and twelve invoices depending on which design
        reads it — and asked at all because the alternative is the worst failure in
        the product: everything maps, nothing is lossy, and one invoice goes out
        with the first customer's name above everybody's lines.
      */}
      {/*
        What it concluded, when there was nothing to ask.
        
        A line rather than silence. Somebody whose spreadsheet holds three
        customers comes to this screen looking for the split; somebody whose
        spreadsheet holds one comes looking for the same control and needs to be
        told it is not there because it is not needed, not left hunting for it.
      */}
      {!result.grouping.options.length && !groupBy ? (
        <p className="small muted">
          This is one document: every row in “{datasetName}” is a line on it. If a file holds several — one invoice per customer, one report per site —
          BroadPaper offers to split it here.
        </p>
      ) : null}

      {result.grouping.options.length || groupBy ? (
        <Card>
          <div className="card__body stack" style={{ gap: 14 }}>
            <div className="stack" style={{ gap: 4 }}>
              <span className="eyebrow">Your data</span>
              <h2>Is this one document, or several?</h2>
              <p className="muted">
                Some columns hold more than one value, which usually means the file covers several documents — one per customer, one per job. BroadPaper
                can split it.
              </p>
            </div>
            <div className="stack" style={{ gap: 8 }}>
              {/* A split that is already saved is shown even when the data no
                  longer suggests it — otherwise a choice made last month on a
                  file that has since changed would be settled beyond reach,
                  which is the whole failure this screen exists to avoid. */}
              {groupBy && !result.grouping.options.some((o) => o.column === groupBy) ? (
                <button type="button" className="tile tile--chosen" style={{ padding: "12px 14px" }} onClick={() => undefined}>
                  <div className="stack" style={{ gap: 2 }}>
                    <strong style={{ fontSize: 15 }}>One document per “{groupBy}”</strong>
                    <span className="small muted">Chosen earlier. This data holds one of them.</span>
                  </div>
                </button>
              ) : null}
              {result.grouping.options.map((option) => (
                <button
                  key={option.column}
                  type="button"
                  className={`tile${groupBy === option.column ? " tile--chosen" : ""}`}
                  style={{ padding: "12px 14px" }}
                  onClick={() => setGroupBy(option.column)}
                >
                  <div className="spread">
                    <div className="stack" style={{ gap: 2 }}>
                      <strong style={{ fontSize: 15 }}>
                        {count(option.groups)} documents, one per {option.label.split(" › ").pop()!.toLowerCase()}
                      </strong>
                      <span className="small muted">
                        {option.example ? `Starting with “${option.example}”` : "From your data"}
                        {option.perRow ? " · one per row, which is unusual" : ""}
                      </span>
                    </div>
                    <Icon name="check" />
                  </div>
                </button>
              ))}
              <button
                type="button"
                className={`tile${groupBy === null ? " tile--chosen" : ""}`}
                style={{ padding: "12px 14px" }}
                onClick={() => setGroupBy(null)}
              >
                <div className="spread">
                  <div className="stack" style={{ gap: 2 }}>
                    <strong style={{ fontSize: 15 }}>One document, with every row on it</strong>
                    <span className="small muted">An invoice whose lines are the rows, or a report covering the whole period.</span>
                  </div>
                  <Icon name="check" />
                </div>
              </button>
            </div>
          </div>
        </Card>
      ) : null}

      {outstanding.map((row) => (
        <Chooser key={row.path} row={row} columns={result.columns} onChoose={(choice) => choose(row.path, choice)} />
      ))}

      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button variant="primary" onClick={() => void save()} busy={saving} disabled={!done} icon="chevron">
          {done ? "See the document" : `${outstanding.length} still to answer`}
        </Button>
        {!done ? (
          <span className="small muted" style={{ alignSelf: "center" }}>
            Each one takes a click.
          </span>
        ) : null}
      </div>

      {/*
        Everything the design is being filled in from, folded away.

        Folded because on a normal spreadsheet it is a dozen correct answers
        nobody asked to read — and openable, and *editable*, because the one
        person who needs it is the person whose document came out wrong, and a
        list they can only read leaves them with nowhere to go.
      */}
      {everything.length ? (
        <section className="stack" style={{ gap: 10 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn btn--quiet" onClick={() => setShowSettled(!showSettled)}>
              <Icon name={showSettled ? "cross" : "check"} />
              {showSettled ? "Hide" : "Show"} what each field is filled in from
            </button>
            {showSettled ? (
              <Button variant="quiet" busy={reloading} onClick={() => void startAgain()} title="Forget these choices and match the data again">
                Start again
              </Button>
            ) : null}
          </div>
          {showSettled ? (
            <Card flat>
              <table className="table">
                <thead>
                  <tr>
                    <th>The design needs</th>
                    <th>Filled in from</th>
                    <th>For example</th>
                    <th style={{ width: 96 }} />
                  </tr>
                </thead>
                <tbody>
                  {everything.map((row) => {
                    const current = mapping[row.path] ?? row.choice;
                    const shown = describe(row, current, result.columns);
                    const open = editing === row.path;
                    return (
                      <Fragment key={row.path}>
                        <tr>
                          <td>
                            <div className="stack" style={{ gap: 2 }}>
                              <strong>{row.label}</strong>
                              {row.group ? <span className="small muted">{row.group}</span> : null}
                            </div>
                          </td>
                          <td>
                            {shown.blank ? (
                              <span className="small muted">Left blank</span>
                            ) : shown.everyRow ? (
                              <Chip tone="good">Every row</Chip>
                            ) : (
                              <div className="stack" style={{ gap: 2 }}>
                                <span>{shown.from}</span>
                                {shown.note ? <span className="small muted">{shown.note}</span> : null}
                              </div>
                            )}
                          </td>
                          <td className="num">
                            <div className="stack" style={{ gap: 2, alignItems: "flex-end" }}>
                              <span>{shown.example}</span>
                              {/* The one thing in this list worth interrupting somebody
                                  about: a field filled in from a column that does not
                                  really hold what it needs. */}
                              {unchangedSince(row, current) && row.warning ? (
                                <span className="small" style={{ color: "var(--amber)", textAlign: "right" }}>
                                  {row.warning}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            {/* A list requirement is the rows and cannot be anything else, so
                                there is nothing to offer and a button would be a dead end. */}
                            {row.isList ? null : (
                              <Button size="sm" variant="quiet" onClick={() => setEditing(open ? null : row.path)}>
                                {open ? "Cancel" : "Change"}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {open ? (
                          <tr>
                            <td colSpan={4} style={{ background: "var(--paper)" }}>
                              <Chooser row={row} columns={result.columns} current={current} onChoose={(choice) => choose(row.path, choice)} />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

/* ------------------------------- what it says ------------------------------ */

/** Whether the answer on screen is still the one the service described. */
function unchangedSince(row: MatchRow, choice: Choice): boolean {
  return JSON.stringify(choice) === JSON.stringify(row.choice);
}

/**
 * How one field's current answer reads.
 *
 * The server described the match it made; this describes whatever the answer is
 * *now*, because the user may have changed it since. Where nothing has changed the
 * server's own example is used, since it was computed from the real rows and the
 * browser only has the columns.
 */
function describe(row: MatchRow, choice: Choice, columns: MatchResult["columns"]): { from: string; note: string; example: string; blank: boolean; everyRow: boolean } {
  const unchanged = JSON.stringify(choice) === JSON.stringify(row.choice);
  const column = choice.kind === "column" || choice.kind === "aggregate" ? columns.find((c) => c.name === choice.column) : undefined;

  if (choice.kind === "none" || (choice.kind === "literal" && (choice.value === null || choice.value === ""))) {
    return { from: "", note: "", example: "", blank: true, everyRow: false };
  }
  if (choice.kind === "rows") return { from: "", note: "", example: unchanged ? row.example : "", blank: false, everyRow: true };
  if (choice.kind === "literal") return { from: "You typed it", note: "", example: String(choice.value), blank: false, everyRow: false };
  return {
    from: column?.label ?? row.chosenColumn ?? "",
    note: choice.kind === "aggregate" ? "added up across all rows" : "",
    example: unchanged ? row.example : (column?.example ?? ""),
    blank: false,
    everyRow: false
  };
}

/* --------------------------------- choosing -------------------------------- */

/**
 * One field's chooser.
 *
 * A card, not a table row, because it is asking something and a table row cannot.
 * The options are buttons showing the value first and the column name second, which
 * is the order somebody reads them in — and the full list of columns is behind
 * "show every column", so the common case is two or three plausible answers rather
 * than forty.
 *
 * Used twice: as the question card for something BroadPaper could not settle, and
 * inline as the editor for something it did. The same component on purpose —
 * changing a confident answer and answering an uncertain one are the same act, and
 * two interfaces for it would eventually disagree about what the options are.
 */
function Chooser({
  row,
  columns,
  current,
  onChoose
}: {
  row: MatchRow;
  columns: MatchResult["columns"];
  current?: Choice;
  onChoose: (choice: Choice) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const [literal, setLiteral] = useState(current?.kind === "literal" && current.value !== null ? String(current.value) : "");
  const suggested = new Set(row.options.map((o) => (o.choice.kind === "column" ? o.choice.column : "")));
  const chosenColumn = current && (current.kind === "column" || current.kind === "aggregate") ? current.column : null;

  /**
   * How a typed value is labelled, which is the difference between somebody
   * setting a VAT rate in two seconds and somebody guessing at what the box wants.
   * A percentage says so, and says what 20 will mean.
   */
  const typing =
    row.type === "percent"
      ? { placeholder: "e.g. 20", hint: "A percentage — type 20 for 20%." }
      : row.type === "currency"
        ? { placeholder: "e.g. 1250.00", hint: "An amount. The currency comes from the design." }
        : row.type === "date" || row.type === "datetime"
          ? { placeholder: "e.g. 30/09/2026", hint: "A date, day first." }
          : { placeholder: "Or type it in", hint: "" };

  return (
    <Card>
      <div className="card__body stack" style={{ gap: 14 }}>
        <div className="stack" style={{ gap: 4 }}>
          {row.group ? <span className="eyebrow">{row.group}</span> : null}
          <h2>{row.label}</h2>
          <p className="muted">{row.because}</p>
        </div>

        {row.options.length ? (
          <div className="stack" style={{ gap: 8 }}>
            <span className="field__label">{current ? "Use one of these instead" : "Which of these is it?"}</span>
            {row.options.map((option) => (
              <button
                key={option.label + JSON.stringify(option.choice)}
                type="button"
                className="tile"
                style={{ padding: "12px 14px" }}
                onClick={() => onChoose(option.choice)}
              >
                <div className="spread">
                  <div className="stack" style={{ gap: 2 }}>
                    {/* The value first. It is what somebody recognises. */}
                    <strong className="num" style={{ fontSize: 16 }}>
                      {option.example || "—"}
                    </strong>
                    <span className="small muted">
                      {option.label}
                      {option.because ? ` · ${option.because}` : ""}
                    </span>
                  </div>
                  <Icon name="check" />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <Note tone="ask">Nothing in your data looks like this. You can type it in instead, or leave it out.</Note>
        )}

        {showAll ? (
          <div className="stack" style={{ gap: 8 }}>
            <span className="field__label">Every column in your data</span>
            <div className="grid grid--3" style={{ gap: 8 }}>
              {columns
                .filter((c) => !suggested.has(c.name) && c.name !== chosenColumn)
                .map((column) => (
                  <button key={column.name} type="button" className="tile" style={{ padding: "10px 12px" }} onClick={() => onChoose({ kind: "column", column: column.name })}>
                    <div className="stack" style={{ gap: 2 }}>
                      <strong className="num small">{column.example || "—"}</strong>
                      <span className="small muted truncate">{column.label}</span>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        ) : null}

        {/* Typing a value is the answer when the data genuinely does not have one —
            a VAT rate, a payment term, a report title, a period nobody put in a
            column. It is the only way some fields can ever be filled in, so it is
            on the card rather than behind anything. */}
        <div className="stack" style={{ gap: 6 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <input
              className="input"
              style={{ maxWidth: 260 }}
              placeholder={typing.placeholder}
              value={literal}
              onChange={(e) => setLiteral(e.target.value)}
              aria-label={`Type a value for ${row.label}`}
            />
            <Button disabled={!literal.trim()} onClick={() => onChoose({ kind: "literal", value: literal.trim() })}>
              Use this
            </Button>
            {!showAll ? (
              <Button variant="quiet" onClick={() => setShowAll(true)}>
                Show every column
              </Button>
            ) : null}
            {!row.required ? (
              <Button variant="quiet" onClick={() => onChoose({ kind: "literal", value: null })}>
                Leave it out
              </Button>
            ) : null}
          </div>
          {typing.hint ? <span className="small muted">{typing.hint}</span> : null}
        </div>
      </div>
    </Card>
  );
}
